(function () {
  'use strict';

  /* Scroller for the discount list: mouse drag-to-scroll (the shared helper in
     eb-drag-scroll.js; touch uses native scrolling), arrow paging with
     end-state, optional auto-slide, and copy-to-clipboard on the coupon code
     chips. CSS scroll-snap settles the row after release. */
  class EbDiscountScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-eb-dl-track]');
      if (!this.track) return;
      this.prevBtn = this.querySelector('[data-eb-dl-prev]');
      this.nextBtn = this.querySelector('[data-eb-dl-next]');

      this.initDrag();
      this.initArrows();
      this.initCopy();
      this.initAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    initDrag() {
      var self = this;
      /* `is-dragging` (pointer-events:none on the chips) only lands once a real
         drag starts, so a plain press still reaches the copy chip. The click
         after a drag is swallowed for everything but that chip — an accidental
         copy is harmless. */
      window.EbDragScroll.attach(this.track, {
        onStart: function () {
          self.pauseAutoplay('drag');
        },
        onEnd: function () {
          self.resumeAutoplay('drag');
        },
        keepClick: function (e) {
          return !!(e.target.closest && e.target.closest('[data-eb-copy]'));
        }
      });
    }

    initArrows() {
      var self = this;
      if (!this.prevBtn && !this.nextBtn) return;

      var reduce =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function page(dir) {
        var amount = self.track.clientWidth * 0.85;
        self.track.scrollBy({
          left: dir * amount,
          behavior: reduce ? 'auto' : 'smooth'
        });
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          page(-1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          page(1);
        });
      }

      this.updateArrows();
      this.track.addEventListener('scroll', function () {
        self.updateArrows();
      });
      window.addEventListener('resize', function () {
        self.updateArrows();
      });
    }

    updateArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      var track = this.track;
      // 1px slack absorbs sub-pixel rounding at the ends.
      var maxScroll = track.scrollWidth - track.clientWidth - 1;
      var scrollable = maxScroll > 1;

      if (this.prevBtn) {
        this.prevBtn.hidden = !scrollable || track.scrollLeft <= 0;
      }
      if (this.nextBtn) {
        this.nextBtn.hidden = !scrollable || track.scrollLeft >= maxScroll;
      }
    }

    /* ── Auto-slide ───────────────────────────────────────────────
       Advances one cell at a time so the row always lands on a card
       boundary (matching scroll-snap), and pauses for anything that means
       the visitor is busy with it: hover, keyboard focus, dragging, a
       hidden tab, or the row being scrolled off screen. Reasons are held
       in a set so two overlapping pauses can't cancel each other out. */
    initAutoplay() {
      if (!this.hasAttribute('data-eb-dl-autoplay')) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var self = this;
      var seconds = parseFloat(this.getAttribute('data-eb-dl-interval'));

      this.apDelay = (seconds > 0 ? seconds : 4) * 1000;
      this.apLoop = this.getAttribute('data-eb-dl-loop') === 'reverse' ? 'reverse' : 'restart';
      this.apDir = 1;
      this.apPaused = {};

      if (this.getAttribute('data-eb-dl-pause-hover') !== 'false') {
        this.addEventListener('mouseenter', function () {
          self.pauseAutoplay('hover');
        });
        this.addEventListener('mouseleave', function () {
          self.resumeAutoplay('hover');
        });
      }

      this.addEventListener('focusin', function () {
        self.pauseAutoplay('focus');
      });
      this.addEventListener('focusout', function () {
        if (!self.contains(document.activeElement)) self.resumeAutoplay('focus');
      });

      // Touch scrolling is native, so there is no drag hook to pause on.
      this.track.addEventListener('touchstart', function () {
        self.pauseAutoplay('touch');
      }, { passive: true });
      this.track.addEventListener('touchend', function () {
        self.resumeAutoplay('touch');
      });

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) self.pauseAutoplay('hidden');
        else self.resumeAutoplay('hidden');
      });

      if ('IntersectionObserver' in window) {
        this.pauseAutoplay('offscreen');
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) self.resumeAutoplay('offscreen');
            else self.pauseAutoplay('offscreen');
          });
        });
        this.observer.observe(this);
      } else {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (!this.apDelay || this.apTimer) return;
      var self = this;
      this.apTimer = window.setInterval(function () {
        self.autoplayStep();
      }, this.apDelay);
    }

    stopAutoplay() {
      if (!this.apTimer) return;
      window.clearInterval(this.apTimer);
      this.apTimer = null;
    }

    pauseAutoplay(reason) {
      if (!this.apDelay) return;
      this.apPaused[reason] = true;
      this.stopAutoplay();
    }

    resumeAutoplay(reason) {
      if (!this.apDelay) return;
      delete this.apPaused[reason];
      if (Object.keys(this.apPaused).length === 0) this.startAutoplay();
    }

    autoplayStep() {
      var track = this.track;
      var maxScroll = track.scrollWidth - track.clientWidth - 1;
      // Nothing to advance through — e.g. every card already fits.
      if (maxScroll <= 1) return;

      if (this.apDir === 1 && track.scrollLeft >= maxScroll) {
        if (this.apLoop === 'reverse') {
          this.apDir = -1;
        } else {
          this.scrollTrackTo(0);
          return;
        }
      } else if (this.apDir === -1 && track.scrollLeft <= 0) {
        this.apDir = 1;
      }

      this.scrollTrackTo(this.cellOffset(this.apDir));
    }

    /* Scroll offset of the next/previous cell boundary. Measured from live rects
       rather than offsetLeft so it doesn't care what the offset parent is. */
    cellOffset(dir) {
      var track = this.track;
      var base = track.getBoundingClientRect().left - track.scrollLeft;
      var cells = track.children;
      var i;

      if (dir === 1) {
        for (i = 0; i < cells.length; i++) {
          var next = cells[i].getBoundingClientRect().left - base;
          if (next > track.scrollLeft + 1) return next;
        }
        return track.scrollWidth;
      }

      for (i = cells.length - 1; i >= 0; i--) {
        var prev = cells[i].getBoundingClientRect().left - base;
        if (prev < track.scrollLeft - 1) return prev;
      }
      return 0;
    }

    scrollTrackTo(left) {
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: left, behavior: reduce ? 'auto' : 'smooth' });
    }

    initCopy() {
      var self = this;
      var buttons = this.querySelectorAll('[data-eb-copy]');
      if (!buttons.length) return;

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.copyCode(btn);
        });
      });
    }

    copyCode(btn) {
      var code = btn.getAttribute('data-eb-copy') || '';
      if (!code) return;

      var flash = function () {
        btn.classList.add('is-copied');
        window.setTimeout(function () {
          btn.classList.remove('is-copied');
        }, 1400);
      };

      // Prefer the async Clipboard API, but it throws or is blocked inside some
      // sandboxed iframes (e.g. the theme editor) — always fall back and always
      // give the visual confirmation regardless.
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(flash, function () {
            fallbackCopy(code);
            flash();
          });
          return;
        }
      } catch (e) {
        /* fall through to execCommand */
      }
      fallbackCopy(code);
      flash();
    }
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.contentEditable = 'true';
    ta.readOnly = false;
    // Keep it in the viewport but invisible so focus()/select() work everywhere
    // (including iOS Safari) without scrolling the page.
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.width = '1px';
    ta.style.height = '1px';
    ta.style.padding = '0';
    ta.style.border = 'none';
    ta.style.opacity = '0';
    document.body.appendChild(ta);

    var ok = false;
    try {
      ta.focus();
      ta.select();
      if (ta.setSelectionRange) ta.setSelectionRange(0, text.length);
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    document.body.removeChild(ta);
    return ok;
  }

  if (!customElements.get('eb-discount-scroller')) {
    customElements.define('eb-discount-scroller', EbDiscountScroller);
  }
})();